/**
 * Navigation item resolution utilities
 */

import { IconComponent } from '@backstage/core-plugin-api';
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@material-ui/core/SvgIcon';
import { StandaloneItem, UtilityItem } from '../types/navigation';
import { resolveIcon } from './icons';
import {
  STANDALONE_DEFAULTS,
  UTILITY_DEFAULTS,
} from './navigationDefaults';

// IconType matches the type returned by icon utilities
type IconType = IconComponent | ComponentType<SvgIconProps>;

export interface ResolvedItem {
  id: string;
  title: string;
  to: string;
  icon?: IconType;
  priority: number;
  enabled: boolean;
}

/**
 * Resolve standalone item from config, Backstage items, and defaults
 */
export function resolveStandaloneItem(
  configItem: StandaloneItem,
  backstageItems: Array<{ title?: string; text?: string; to?: string; icon?: IconComponent }>,
  isFromUserConfig: boolean,
): ResolvedItem | null {
  // Check if explicitly disabled
  if (configItem.enabled === false) {
    return null;
  }

  // Find matching Backstage item
  const backstageItem = backstageItems.find(
    item => (item.title || item.text || '').toLowerCase() === configItem.id.toLowerCase()
  );

  // Zero-config mode: Only show items that exist in Backstage
  if (!isFromUserConfig && !backstageItem) {
    return null;
  }

  // Get defaults for this item
  const defaults = STANDALONE_DEFAULTS[configItem.id] || {};

  // Merge: config overrides > Backstage > defaults
  const title = configItem.title || backstageItem?.title || defaults.title || configItem.id;
  const to = configItem.to || backstageItem?.to || defaults.to || `/${configItem.id}`;
  const iconValue = configItem.icon || backstageItem?.icon || defaults.icon;
  const icon = resolveIcon(iconValue);
  const priority = configItem.priority ?? 100;

  return {
    id: configItem.id,
    title,
    to,
    icon,
    priority,
    enabled: true,
  };
}

/**
 * Resolve utility item from config and defaults
 */
export function resolveUtilityItem(configItem: UtilityItem): ResolvedItem | null {
  // Check if explicitly disabled
  if (configItem.enabled === false) {
    return null;
  }

  // Get defaults for this item
  const defaults = UTILITY_DEFAULTS[configItem.id] || {};

  // Merge: config overrides > defaults
  const title = configItem.title || defaults.title || configItem.id;
  const to = configItem.to || defaults.to || `/${configItem.id}`;
  const iconValue = configItem.icon || defaults.icon;
  const icon = resolveIcon(iconValue);
  const priority = configItem.priority ?? 100;

  return {
    id: configItem.id,
    title,
    to,
    icon,
    priority,
    enabled: true,
  };
}
